import { apiAdmin } from "./api";
import { mapCalendarItem, getCategoryApiValue } from "../utils/agendaUtils";

/**
 * Fetch calendar list from backend API (/endpoint/user/calendar)
 * @param {Object} options
 * @param {string} options.selectedCategory - Frontend category ('all', 'webinar', 'clinical_session', 'event', 'mentorship')
 * @param {string} options.entityType - Optional backend-ready entity type value, such as 'webinar,event'
 * @param {number} options.page - Page number
 * @param {number} options.perPage - Items per page
 */
export const getCalendarEvents = async ({
  selectedCategory,
  entityType,
  event_type,
  page = 1,
  perPage = 16,
} = {}) => {
  try {
    const rawCategoryOrType =
      event_type !== undefined
        ? event_type
        : entityType !== undefined
        ? entityType
        : selectedCategory !== undefined
        ? selectedCategory
        : "event,webinar";

    const resolvedEntityType = getCategoryApiValue(rawCategoryOrType) || "event,webinar";

    const params = {
      paginate: 1,
      per_page: perPage,
      limit: perPage,
      page,
      entity_type: resolvedEntityType,
    };

    const response = await apiAdmin.get("user/calendar", { params });

    if (response?.data?.status) {
      const resPayload = response.data.data ?? response.data ?? {};
      const listContainer = resPayload.list ?? resPayload;
      const rawListData = listContainer?.data ?? listContainer ?? [];
      const rawList = Array.isArray(rawListData)
        ? rawListData
        : rawListData && typeof rawListData === "object"
        ? Object.values(rawListData)
        : [];
      const mappedEvents = rawList.map(mapCalendarItem).filter(Boolean);

      // Extract direct upcoming array from backend response if present
      const rawUpcoming = resPayload.upcoming ?? [];
      const rawUpcomingList = Array.isArray(rawUpcoming)
        ? rawUpcoming
        : rawUpcoming && typeof rawUpcoming === "object"
        ? Object.values(rawUpcoming)
        : [];
      const mappedUpcoming = rawUpcomingList.map(mapCalendarItem).filter(Boolean);

      // Extract suggestions array from backend response if present
      const rawSuggestion = resPayload.suggestion ?? [];
      const rawSuggestionList = Array.isArray(rawSuggestion)
        ? rawSuggestion
        : rawSuggestion && typeof rawSuggestion === "object"
        ? Object.values(rawSuggestion)
        : [];
      const mappedSuggestion = rawSuggestionList.map(mapCalendarItem).filter(Boolean);

      // Extract ads object from backend response
      const rawAds = resPayload.ads ?? response.data?.ads ?? null;

      return {
        status: true,
        data: mappedEvents,
        upcoming: mappedUpcoming,
        suggestion: mappedSuggestion,
        ads: rawAds,
        raw: resPayload,
        pagination: {
          currentPage: Number(listContainer?.current_page || page),
          lastPage: Number(listContainer?.last_page || 1),
          total: Number(listContainer?.total || mappedEvents.length),
          nextPageUrl: listContainer?.next_page_url || null,
        },
      };
    }

    return {
      status: false,
      data: [],
      error: response?.data?.message || "Failed to fetch calendar events",
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        nextPageUrl: null,
      },
    };
  } catch (error) {
    console.error("Error in getCalendarEvents:", error);
    throw error;
  }
};

/**
 * Load the complete event record before opening the agenda edit drawer.
 * Calendar responses intentionally contain only the fields needed for a card.
 */
export const getAgendaEventDetail = async (eventId) => {
  if (!eventId) {
    throw new Error("An event id is required to load event details");
  }

  const cleanId = String(eventId).replace(/^(?:session-|evt-|webinar-)/i, "");

  // Try post detail first (covers webinars & clinical sessions)
  try {
    const postRes = await apiAdmin.get(`user/post/detail/${cleanId}`);
    if (postRes?.data?.status && postRes?.data?.data) {
      return postRes.data.data;
    } else if (postRes?.data?.data) {
      return postRes.data.data;
    }
  } catch (postErr) {
    // If post detail fails, try event detail
  }

  try {
    const response = await apiAdmin.get(`user/event/detail/${cleanId}`);
    const responseData = response?.data;

    if (responseData?.status && responseData?.data) {
      return responseData.data;
    } else if (responseData?.data) {
      return responseData.data;
    }

    throw new Error(
      responseData?.action ||
        responseData?.message ||
        "Unable to load event details"
    );
  } catch (error) {
    console.error("Error loading agenda event details:", error);
    throw error;
  }
};

/**
 * Send Webinar Invitation
 */
export const sendWebinarInvite = async (webinarId, userId) => {
  try {
    const response = await apiAdmin.get(`user/post/webinar/invite/${webinarId}/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error sending webinar invite:", error);
    throw error;
  }
};

/**
 * Send Clinical Session Invitation
 */
export const sendClinicalSessionInvite = async (sessionId, userId) => {
  try {
    const response = await apiAdmin.get(`user/post/clinical-session/invite/${sessionId}/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error sending clinical session invite:", error);
    throw error;
  }
};
